import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FacultyDirectoryPage } from './pages/FacultyDirectoryPage';
import { FacultyProfilePage } from './pages/FacultyProfilePage';
import { SchoolContextRecoveryCard } from '../../components/app-shell/SchoolContextRecoveryCard';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';
import { useDomainRouterMutation, useDomainRouterQuery } from '../../state/domainRouterHooks';
import { useSchoolContext } from '../../state/useSchoolContext';
import type {
  Division,
  FacultyAssetDescriptor,
  FacultyCommunication,
  FacultyCommunicationDraft,
  FacultyCommunicationStatus,
  FacultyMember,
  FacultyNote,
  FacultyNoteDraft,
  FacultyProfileDraft,
  NoteCategory,
  TeachingAssignment,
} from './types/faculty';

type FacultyRecord = {
  id: string;
  orgId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  hebrewFirstName: string;
  hebrewLastName: string;
  dateOfBirth: string;
  gender?: FacultyMember['gender'];
  profilePhoto: string;
  profilePhotoResourceId?: string;
  personalEmail: string;
  workEmail: string;
  cellPhone: string;
  homePhone: string;
  address?: FacultyMember['address'];
  employeeId: string;
  hireDate: string;
  position: FacultyMember['position'];
  employmentType: FacultyMember['employmentType'];
  status: FacultyMember['status'];
  divisions: Division[];
  gradeLevels: string[];
  subjectLabels: string[];
  homeroom: string;
  reportsTo: string;
  tags: string[];
};

type AssignmentRecord = {
  assignmentType: string;
  divisionAssignment?: Record<string, unknown>;
  teachingAssignment?: Record<string, unknown>;
  schedule?: Record<string, unknown>;
  emergencyContact?: Record<string, unknown>;
  updatedAt?: unknown;
  createdAt?: unknown;
};

type NoteRecord = {
  id: string;
  content: string;
  author: string;
  category: NoteCategory;
  important: boolean;
  timestamp: string | number;
  attachments: FacultyAssetDescriptor[];
};

type CommunicationRecord = {
  id: string;
  sender: string;
  subject: string;
  content: string;
  recipients: string[];
  attachments: FacultyAssetDescriptor[];
  timestamp: string | number;
  status?: FacultyCommunicationStatus;
  outboxId?: string;
  providerMessageId?: string;
  lastError?: string | null;
};

type TargetWeek = 'this' | 'next';
type UploadKind = 'profile-photo' | 'note-attachment' | 'communication-attachment';

const DEFAULT_ROUTER_MODE_PATH = '/school/faculty';
const scheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;
const dayIndexToLabel = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

const scheduleDayMap: Record<string, (typeof scheduleDays)[number] | null> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: null,
  sun: null,
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? (value as Record<string, unknown>[]) : [];

const readString = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

const readErrorMessage = (value: unknown): string => {
  const record = asRecord(value);
  return readString(record.message, readString(record.error, 'Unknown error'));
};

const readArrayStrings = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => String(entry ?? '').trim()).filter(Boolean);
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const readTimestampValue = (value: unknown, fallback: string | number): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return fallback;
};

const uniqueStrings = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

const toDateMs = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 9_999_999_999 ? value : value * 1000;
    return Number.isFinite(ms) ? ms : 0;
  }
  const parsed = new Date(String(value ?? '').trim()).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDateInputValue = (value: unknown): string => {
  const ms = toDateMs(value);
  if (!ms) return '';
  return new Date(ms).toISOString().slice(0, 10);
};

const toUnixTimestamp = (value: string | undefined): number | null => {
  const raw = readString(value);
  if (!raw) return null;
  const parsed = new Date(raw).getTime();
  if (!Number.isFinite(parsed)) return null;
  return Math.floor(parsed / 1000);
};

const normalizeDivision = (value: unknown): Division | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'boys' || normalized === 'boy') return 'Boys';
  if (normalized === 'girls' || normalized === 'girl') return 'Girls';
  if (['preschool', 'pre-school', 'prek', 'pre-k'].includes(normalized)) return 'Preschool';
  return null;
};

const normalizeDivisions = (value: unknown, primaryDivisionId?: unknown): Division[] => {
  const explicit = readArrayStrings(value)
    .map((entry) => normalizeDivision(entry))
    .filter((entry): entry is Division => Boolean(entry));
  const primary = normalizeDivision(primaryDivisionId);
  return uniqueStrings([...(primary ? [primary] : []), ...explicit]) as Division[];
};

const divisionToDomainId = (division: Division): string => {
  if (division === 'Boys') return 'boys';
  if (division === 'Girls') return 'girls';
  return 'preschool';
};

const normalizeStatus = (value: unknown): FacultyMember['status'] => {
  const text = String(value ?? '').trim().toLowerCase().replace(/[\s()-]+/g, '_');
  if (!text) return 'active';
  if (text === 'invited' || text === 'new_hire' || text === 'invited_new_hire') return 'invited';
  if (text === 'active') return 'active';
  if (text.includes('medical')) return 'on_leave_medical';
  if (text.includes('maternity')) return 'on_leave_maternity';
  if (text.includes('sabbatical')) return 'on_leave_sabbatical';
  if (text.includes('leave')) return 'on_leave_other';
  if (text === 'inactive') return 'inactive';
  if (text === 'substitute') return 'substitute';
  if (text === 'temporary' || text === 'temp') return 'temporary';
  if (text === 'terminated') return 'terminated';
  return 'active';
};

const normalizePosition = (value: unknown): FacultyMember['position'] => {
  const text = String(value ?? '').trim().toLowerCase();
  if (text.includes('leader') || text.includes('principal') || text.includes('director') || text.includes('head')) {
    return 'Leadership';
  }
  if (text.includes('aide')) return "Teacher's Aide";
  if (text.includes('special')) return 'Specialist';
  if (text.includes('substitute')) return 'Substitute';
  if (text.includes('temporary')) return 'Temporary';
  if (text.includes('teacher')) return 'Teacher';
  return 'Other';
};

const normalizeEmploymentType = (value: unknown): FacultyMember['employmentType'] => {
  const text = String(value ?? '').trim().toLowerCase();
  return text === 'part_time' || text === 'part-time' ? 'Part-time' : 'Full-time';
};

const toDomainEmploymentType = (
  value: FacultyMember['employmentType'] | undefined,
): 'full_time' | 'part_time' | null => {
  if (!value) return null;
  return value === 'Part-time' ? 'part_time' : 'full_time';
};

const normalizeGender = (value: unknown): FacultyMember['gender'] | undefined => {
  const text = String(value ?? '').trim().toLowerCase();
  if (text === 'female') return 'Female';
  if (text === 'male') return 'Male';
  return undefined;
};

const toDomainGender = (value: FacultyMember['gender'] | undefined): 'male' | 'female' | null => {
  if (!value) return null;
  return value === 'Female' ? 'female' : 'male';
};

const normalizeNoteCategory = (value: unknown): NoteCategory => {
  const raw = readString(value, 'General').toLowerCase();
  if (raw.includes('perform')) return 'Performance';
  if (raw === 'hr') return 'HR';
  if (raw.includes('other')) return 'Other';
  return 'General';
};

const normalizeCommunicationStatus = (value: unknown): FacultyCommunicationStatus | undefined => {
  const raw = readString(value).toLowerCase();
  if (raw === 'queued' || raw === 'processing' || raw === 'sent' || raw === 'failed') {
    return raw;
  }
  return undefined;
};

const formatDisplayDate = (value: string | number): string => {
  const ms = toDateMs(value);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isRenderableMediaUrl = (value: string): boolean =>
  value.startsWith('http://')
  || value.startsWith('https://')
  || value.startsWith('data:')
  || value.startsWith('blob:');

const mapAddress = (value: unknown): FacultyMember['address'] | undefined => {
  if (typeof value === 'string') {
    const parts = value.split(',').map((entry) => entry.trim()).filter(Boolean);
    if (parts.length === 0) return undefined;
    const [street = '', city = '', stateZip = ''] = parts;
    const [state = '', zip = ''] = stateZip.split(/\s+/, 2);
    return { street, city, state, zip };
  }

  const record = asRecord(value);
  if (!Object.keys(record).length) return undefined;

  const street = readString(record.street);
  const city = readString(record.city);
  const state = readString(record.state);
  const zip = readString(record.zip ?? record.zipCode);
  if (!street && !city && !state && !zip) return undefined;

  return { street, city, state, zip };
};

const toAddressInput = (value: FacultyMember['address'] | undefined) => {
  if (!value) return null;
  const street = readString(value.street);
  const city = readString(value.city);
  const state = readString(value.state);
  const zip = readString(value.zip);
  if (!street && !city && !state && !zip) return null;
  return { street, city, state, zip };
};

const mapAssetDescriptors = (
  value: unknown,
  attachmentName?: unknown,
): FacultyAssetDescriptor[] => {
  const rawArray = Array.isArray(value) ? value : [];
  const mapped = rawArray.flatMap((entry) => {
    if (typeof entry === 'string') {
      const fileName = readString(entry);
      return fileName
        ? [{ resourceId: `legacy:${fileName}`, fileName }]
        : [];
    }

    const record = asRecord(entry);
    const fileName = readString(record.fileName ?? record.name);
    const resourceId = readString(record.resourceId, fileName ? `legacy:${fileName}` : '');
    if (!resourceId || !fileName) return [];

    return [{
      resourceId,
      fileName,
      contentType: readString(record.contentType) || undefined,
      sizeBytes: readNumber(record.sizeBytes) ?? undefined,
      storageKey: readString(record.storageKey) || undefined,
      uploadedAt: readTimestampValue(record.uploadedAt, ''),
    }];
  });

  if (mapped.length > 0) return mapped;

  const fallbackName = readString(attachmentName);
  if (!fallbackName) return [];
  return [{ resourceId: `legacy:${fallbackName}`, fileName: fallbackName }];
};

const sortByNewest = <T extends { updatedAt?: unknown; createdAt?: unknown }>(items: T[]) =>
  [...items].sort((left, right) => {
    const leftMs = Math.max(toDateMs(left.updatedAt), toDateMs(left.createdAt));
    const rightMs = Math.max(toDateMs(right.updatedAt), toDateMs(right.createdAt));
    return rightMs - leftMs;
  });

const mapFacultyRecord = (
  item: Record<string, unknown>,
  index: number,
  fallbackOrgId = '',
): FacultyRecord => {
  const firstName = readString(item.firstName, readString(item.givenName, 'Faculty'));
  const lastName = readString(item.lastName, readString(item.familyName, `${index + 1}`));
  const rawPhoto = readString(item.profilePhotoUrl, readString(item.photoUrl));
  const renderablePhoto = isRenderableMediaUrl(rawPhoto) ? rawPhoto : '';
  const resourceId = readString(
    item.resourceId,
    !renderablePhoto && rawPhoto ? rawPhoto : '',
  );
  const gradeLevels = uniqueStrings([
    ...readArrayStrings(item.gradeLevels),
    ...readArrayStrings(item.grades),
  ]);
  const subjectLabels = uniqueStrings([
    ...readArrayStrings(item.subjectLabels),
    ...readArrayStrings(item.subjects),
  ]);

  return {
    id: readString(item.id, `faculty-${index + 1}`),
    orgId: readString(item.orgId, fallbackOrgId),
    firstName,
    middleName: readString(item.middleName),
    lastName,
    preferredName: readString(item.preferredName, readString(item.displayName, firstName)),
    hebrewFirstName: readString(item.hebrewFirstName),
    hebrewLastName: readString(item.hebrewLastName),
    dateOfBirth: toDateInputValue(item.dateOfBirth),
    gender: normalizeGender(item.gender),
    profilePhoto: renderablePhoto,
    profilePhotoResourceId: resourceId || undefined,
    personalEmail: readString(item.personalEmail),
    workEmail: readString(item.email),
    cellPhone: readString(item.phone, readString(item.cellPhone)),
    homePhone: readString(item.homePhone),
    address: mapAddress(item.address),
    employeeId: readString(item.employeeId, readString(item.externalId)),
    hireDate: toDateInputValue(item.hireDate),
    position: normalizePosition(item.position ?? item.title ?? item.role),
    employmentType: normalizeEmploymentType(item.employmentType),
    status: normalizeStatus(item.status),
    divisions: normalizeDivisions(item.divisions, item.primaryDivisionId),
    gradeLevels,
    subjectLabels,
    homeroom: readString(item.homeroom),
    reportsTo: readString(item.reportsTo),
    tags: readArrayStrings(item.tags),
  };
};

const facultyIdentityKey = (record: FacultyRecord): string =>
  readString(
    record.employeeId,
    readString(
      record.workEmail,
      `${record.orgId}:${record.firstName}:${record.lastName}:${record.position}`,
    ),
  ).toLowerCase();

const mapAssignmentRecords = (rawItems: Record<string, unknown>[]): AssignmentRecord[] =>
  rawItems.map((item) => ({
    assignmentType: readString(item.assignmentType),
    divisionAssignment: Object.keys(asRecord(item.divisionAssignment)).length
      ? asRecord(item.divisionAssignment)
      : undefined,
    teachingAssignment: Object.keys(asRecord(item.teachingAssignment)).length
      ? asRecord(item.teachingAssignment)
      : undefined,
    schedule: Object.keys(asRecord(item.schedule)).length
      ? asRecord(item.schedule)
      : undefined,
    emergencyContact: Object.keys(asRecord(item.emergencyContact)).length
      ? asRecord(item.emergencyContact)
      : undefined,
    updatedAt: item.updatedAt,
    createdAt: item.createdAt,
  }));

const mapNotes = (rawItems: Record<string, unknown>[]): NoteRecord[] =>
  rawItems
    .map((entry, index) => ({
      id: readString(entry.id, `note-${index + 1}`),
      content: readString(entry.body, readString(entry.content)),
      author: readString(entry.authorLabel, readString(entry.authorId)),
      category: normalizeNoteCategory(entry.category),
      important: Boolean(entry.isImportant),
      timestamp: readTimestampValue(entry.createdAt, new Date().toISOString()),
      attachments: mapAssetDescriptors(entry.attachments, entry.attachmentName),
    }))
    .filter((entry) => Boolean(entry.content));

const mapCommunications = (rawItems: Record<string, unknown>[]): CommunicationRecord[] =>
  rawItems
    .map((entry, index) => ({
      id: readString(entry.id, `communication-${index + 1}`),
      sender: readString(entry.senderLabel, readString(entry.createdBy)),
      subject: readString(entry.subject),
      content: readString(entry.body, readString(entry.content)),
      recipients: uniqueStrings([
        ...readArrayStrings(entry.recipients),
        readString(entry.recipient),
      ]),
      attachments: mapAssetDescriptors(entry.attachments),
      timestamp: readTimestampValue(
        entry.sentAt,
        readTimestampValue(entry.createdAt, new Date().toISOString()),
      ),
      status: normalizeCommunicationStatus(entry.status),
      outboxId: readString(entry.outboxId) || undefined,
      providerMessageId: readString(entry.providerMessageId) || undefined,
      lastError: readString(entry.lastError ?? entry.error) || undefined,
    }))
    .filter((entry) => Boolean(entry.subject || entry.content));

const mapNoteRecordsToFacultyNotes = (notes: NoteRecord[]): FacultyNote[] =>
  [...notes]
    .sort((left, right) => toDateMs(right.timestamp) - toDateMs(left.timestamp))
    .map((note) => ({
      id: note.id,
      content: note.content,
      author: note.author,
      timestamp: formatDisplayDate(note.timestamp),
      category: note.category,
      isImportant: note.important,
      attachments: note.attachments,
    }));

const mapCommunicationRecordsToFacultyCommunications = (
  communications: CommunicationRecord[],
): FacultyCommunication[] =>
  [...communications]
    .sort((left, right) => toDateMs(right.timestamp) - toDateMs(left.timestamp))
    .map((communication) => ({
      id: communication.id,
      sender: communication.sender,
      subject: communication.subject || 'Untitled communication',
      date: formatDisplayDate(communication.timestamp),
      preview: communication.content.slice(0, 120),
      fullContent: communication.content,
      recipients: communication.recipients,
      attachments: communication.attachments,
      status: communication.status,
      outboxId: communication.outboxId,
      providerMessageId: communication.providerMessageId,
      lastError: communication.lastError,
    }));

const blockToScheduleEntry = (
  block: Record<string, unknown>,
  fallbackPeriod: number,
): TeachingAssignment['schedule'][string][number] | null => {
  const label = readString(block.subjectLabel, readString(block.label));
  const rawDay = readString(block.day).toLowerCase();
  if (!scheduleDayMap[rawDay]) return null;

  const typeToken = readString(block.blockType).toLowerCase();
  const type: TeachingAssignment['schedule'][string][number]['type'] =
    typeToken === 'prep'
      ? 'prep'
      : typeToken === 'duty'
        ? 'duty'
        : label.toLowerCase().includes('prep')
          ? 'prep'
          : label.toLowerCase().includes('duty')
            ? 'duty'
            : 'teaching';

  return {
    period: readNumber(block.period) ?? fallbackPeriod,
    subject: label || readString(block.gradeLabel, 'Scheduled block'),
    grade: readString(block.gradeLabel),
    room: readString(block.room, readString(block.location)) || undefined,
    type,
  };
};

const startOfWeek = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  return next;
};

const isSameWeek = (timestamp: unknown, targetWeekStart: Date): boolean => {
  const ms = toDateMs(timestamp);
  if (!ms) return false;
  const targetEnd = new Date(targetWeekStart);
  targetEnd.setDate(targetEnd.getDate() + 7);
  return ms >= targetWeekStart.getTime() && ms < targetEnd.getTime();
};

const mapScheduleEntriesForWeek = (
  assignments: AssignmentRecord[],
  week: TargetWeek,
): TeachingAssignment['schedule'] => {
  const scheduleAssignments = sortByNewest(
    assignments
      .map((item) => asRecord(item.schedule))
      .filter((entry) => Object.keys(entry).length > 0),
  );

  const schedule: TeachingAssignment['schedule'] = {};
  for (const day of scheduleDays) {
    schedule[day] = [];
  }

  const today = new Date();
  const targetWeekStart = startOfWeek(today);
  if (week === 'next') {
    targetWeekStart.setDate(targetWeekStart.getDate() + 7);
  }

  for (const scheduleAssignment of scheduleAssignments) {
    const template = asArray(scheduleAssignment.template);
    const dayCounts = new Map<string, number>();

    for (const block of template) {
      const day = scheduleDayMap[readString(block.day).toLowerCase()];
      if (!day) continue;

      const count = dayCounts.get(day) ?? 0;
      const entry = blockToScheduleEntry(block, count + 1);
      if (!entry) continue;

      dayCounts.set(day, Math.max(count + 1, entry.period));
      schedule[day].push(entry);
    }

    const exceptions = asArray(scheduleAssignment.exceptions);
    for (const exception of exceptions) {
      if (!isSameWeek(exception.date, targetWeekStart)) continue;
      const dayLabel = dayIndexToLabel[new Date(toDateMs(exception.date)).getDay()];
      if (!scheduleDays.includes(dayLabel as (typeof scheduleDays)[number])) continue;
      const day = dayLabel as (typeof scheduleDays)[number];
      const overrideBlocks = asArray(exception.overrideBlocks);
      if (Boolean(exception.cancelled) && overrideBlocks.length === 0) {
        schedule[day] = [];
        continue;
      }
      if (overrideBlocks.length > 0) {
        schedule[day] = overrideBlocks
          .map((block, index) => blockToScheduleEntry({ ...block, day }, index + 1))
          .filter((entry): entry is TeachingAssignment['schedule'][string][number] => Boolean(entry))
          .sort((left, right) => left.period - right.period);
      }
    }
  }

  for (const day of scheduleDays) {
    schedule[day] = [...schedule[day]].sort((left, right) => left.period - right.period);
  }

  return schedule;
};

const buildTeachingAssignment = (
  record: FacultyRecord,
  assignmentRecords: AssignmentRecord[],
): TeachingAssignment | undefined => {
  const divisionAssignments = sortByNewest(
    assignmentRecords
      .map((item) => asRecord(item.divisionAssignment))
      .filter((entry) => Object.keys(entry).length > 0),
  );
  const teachingAssignments = sortByNewest(
    assignmentRecords
      .map((item) => asRecord(item.teachingAssignment))
      .filter((entry) => Object.keys(entry).length > 0),
  );

  const assignmentDivisions = divisionAssignments
    .map((entry) => normalizeDivision(entry.divisionId ?? entry.divisionLabel))
    .filter((entry): entry is Division => Boolean(entry));
  const divisions = uniqueStrings([...record.divisions, ...assignmentDivisions]) as Division[];

  const grades = uniqueStrings([
    ...record.gradeLevels,
    ...teachingAssignments.map((entry) => readString(entry.gradeLabel)),
  ]);
  const subjects = uniqueStrings([
    ...record.subjectLabels,
    ...teachingAssignments.map((entry) => readString(entry.subjectLabel, readString(entry.courseId))),
  ]);
  const homeroom = readString(
    record.homeroom,
    teachingAssignments.map((entry) => readString(entry.homeroom)).find(Boolean) ?? '',
  );
  const classes = teachingAssignments
    .map((entry) => ({
      grade: readString(entry.gradeLabel),
      subject: readString(entry.subjectLabel, readString(entry.courseId, readString(entry.sectionId))),
      studentCount: readNumber(entry.studentCount) ?? 0,
      sectionId: readString(entry.sectionId) || undefined,
      courseId: readString(entry.courseId) || undefined,
    }))
    .filter((entry) => Boolean(entry.grade || entry.subject));

  const schedule = mapScheduleEntriesForWeek(assignmentRecords, 'this');
  const nextWeekSchedule = mapScheduleEntriesForWeek(assignmentRecords, 'next');
  const hasSchedule = Object.values(schedule).some((entries) => entries.length > 0);
  const hasNextWeekSchedule = Object.values(nextWeekSchedule).some((entries) => entries.length > 0);
  const hasData =
    divisions.length > 0
    || grades.length > 0
    || subjects.length > 0
    || classes.length > 0
    || hasSchedule
    || hasNextWeekSchedule
    || Boolean(homeroom);
  if (!hasData) return undefined;

  return {
    divisions,
    grades,
    subjects,
    homeroom: homeroom || undefined,
    schedule,
    nextWeekSchedule: hasNextWeekSchedule ? nextWeekSchedule : undefined,
    classes,
  };
};

const resolveEmergencyContact = (assignments: AssignmentRecord[]): FacultyMember['emergencyContact'] => {
  const emergencyContacts = sortByNewest(
    assignments
      .map((item) => asRecord(item.emergencyContact))
      .filter((entry) => Object.keys(entry).length > 0),
  );
  const latest = emergencyContacts[0];
  if (!latest) return undefined;

  return {
    name: readString(latest.name),
    relationship: readString(latest.relationship),
    phone: readString(latest.phone),
    alternatePhone: readString(latest.alternatePhone) || undefined,
  };
};

const facultyRecordToFacultyMember = (
  record: FacultyRecord,
  extras?: {
    assignments?: AssignmentRecord[];
    notes?: NoteRecord[];
    communications?: CommunicationRecord[];
  },
): FacultyMember => {
  const assignments = extras?.assignments ?? [];
  const notes = extras?.notes ?? [];
  const communications = extras?.communications ?? [];
  const teachingAssignment = buildTeachingAssignment(record, assignments);

  return {
    id: record.id,
    firstName: record.firstName,
    middleName: record.middleName || undefined,
    lastName: record.lastName,
    hebrewFirstName: record.hebrewFirstName || undefined,
    hebrewLastName: record.hebrewLastName || undefined,
    preferredName: record.preferredName || undefined,
    dateOfBirth: record.dateOfBirth || undefined,
    gender: record.gender,
    profilePhoto: record.profilePhoto || undefined,
    profilePhotoResourceId: record.profilePhotoResourceId,
    personalEmail: record.personalEmail || undefined,
    workEmail: record.workEmail,
    cellPhone: record.cellPhone,
    homePhone: record.homePhone || undefined,
    address: record.address,
    employeeId: record.employeeId || record.id,
    hireDate: record.hireDate || '',
    position: record.position,
    employmentType: record.employmentType,
    status: record.status,
    divisions: record.divisions,
    reportsTo: record.reportsTo || undefined,
    tags: record.tags,
    emergencyContact: resolveEmergencyContact(assignments),
    teachingAssignment,
    notes: mapNoteRecordsToFacultyNotes(notes),
    communications: mapCommunicationRecordsToFacultyCommunications(communications),
  };
};

const mergeFacultyRecord = (
  base: FacultyRecord,
  updates: Partial<FacultyMember>,
): FacultyRecord => ({
  ...base,
  firstName: updates.firstName ?? base.firstName,
  middleName: updates.middleName ?? base.middleName,
  lastName: updates.lastName ?? base.lastName,
  preferredName: updates.preferredName ?? base.preferredName,
  hebrewFirstName: updates.hebrewFirstName ?? base.hebrewFirstName,
  hebrewLastName: updates.hebrewLastName ?? base.hebrewLastName,
  dateOfBirth: updates.dateOfBirth ?? base.dateOfBirth,
  gender: updates.gender ?? base.gender,
  profilePhoto: updates.profilePhoto ?? base.profilePhoto,
  profilePhotoResourceId: updates.profilePhotoResourceId ?? base.profilePhotoResourceId,
  personalEmail: updates.personalEmail ?? base.personalEmail,
  workEmail: updates.workEmail ?? base.workEmail,
  cellPhone: updates.cellPhone ?? base.cellPhone,
  homePhone: updates.homePhone ?? base.homePhone,
  address: updates.address ?? base.address,
  employeeId: updates.employeeId ?? base.employeeId,
  hireDate: updates.hireDate ?? base.hireDate,
  position: updates.position ?? base.position,
  employmentType: updates.employmentType ?? base.employmentType,
  status: updates.status ?? base.status,
  divisions: updates.divisions ?? base.divisions,
  gradeLevels: updates.teachingAssignment?.grades ?? base.gradeLevels,
  subjectLabels: updates.teachingAssignment?.subjects ?? base.subjectLabels,
  homeroom: updates.teachingAssignment?.homeroom ?? base.homeroom,
  reportsTo: updates.reportsTo ?? base.reportsTo,
  tags: updates.tags ?? base.tags,
});

const upsertFacultyRecord = (records: FacultyRecord[], nextRecord: FacultyRecord): FacultyRecord[] => [
  nextRecord,
  ...records.filter((record) => record.id !== nextRecord.id),
];

const inferFacultyIdFromPath = (pathname: string): string => {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const schoolDynamic = normalized.match(/^\/school\/faculty\/([^/]+)(?:\/(?:teaching-assignment|notes|communications))?$/);
  if (schoolDynamic?.[1] && !['directory', 'profile'].includes(schoolDynamic[1])) {
    return decodeURIComponent(schoolDynamic[1]);
  }
  const adminDynamic = normalized.match(/^\/admin\/faculty\/([^/]+)(?:\/(?:teaching-assignment|notes|communications))?$/);
  if (adminDynamic?.[1] && !['directory', 'profile'].includes(adminDynamic[1])) {
    return decodeURIComponent(adminDynamic[1]);
  }
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  return params.get('facultyId') ?? '';
};

const nullableTrim = (value: string | undefined) => {
  const trimmed = readString(value);
  return trimmed || null;
};

const buildProfileInput = (
  next: Partial<FacultyMember>,
  current?: FacultyMember,
) => {
  const divisions = (next.divisions ?? current?.divisions ?? []).filter(Boolean);
  const gradeLevels = next.teachingAssignment?.grades ?? current?.teachingAssignment?.grades ?? [];
  const subjectLabels = next.teachingAssignment?.subjects ?? current?.teachingAssignment?.subjects ?? [];
  const homeroom = next.teachingAssignment?.homeroom ?? current?.teachingAssignment?.homeroom ?? '';

  return {
    status: next.status ?? current?.status ?? 'active',
    firstName: readString(next.firstName, current?.firstName),
    lastName: readString(next.lastName, current?.lastName),
    displayName: readString(next.preferredName, next.firstName ?? current?.preferredName ?? current?.firstName),
    middleName: readString(next.middleName, current?.middleName),
    preferredName: readString(next.preferredName, current?.preferredName),
    hebrewFirstName: readString(next.hebrewFirstName, current?.hebrewFirstName),
    hebrewLastName: readString(next.hebrewLastName, current?.hebrewLastName),
    dateOfBirth: toUnixTimestamp(next.dateOfBirth ?? current?.dateOfBirth),
    gender: toDomainGender(next.gender ?? current?.gender),
    profilePhotoUrl: readString(next.profilePhoto, current?.profilePhoto),
    email: nullableTrim(next.workEmail ?? current?.workEmail),
    personalEmail: nullableTrim(next.personalEmail ?? current?.personalEmail),
    phone: nullableTrim(next.cellPhone ?? current?.cellPhone),
    homePhone: nullableTrim(next.homePhone ?? current?.homePhone),
    address: toAddressInput(next.address ?? current?.address),
    employeeId: nullableTrim(next.employeeId ?? current?.employeeId),
    hireDate: toUnixTimestamp(next.hireDate ?? current?.hireDate),
    position: next.position ?? current?.position ?? 'Teacher',
    employmentType: toDomainEmploymentType(next.employmentType ?? current?.employmentType),
    divisions,
    gradeLevels,
    subjectLabels,
    homeroom: nullableTrim(homeroom),
    reportsTo: nullableTrim(next.reportsTo ?? current?.reportsTo),
    tags: uniqueStrings(next.tags ?? current?.tags ?? []),
    resourceId: nullableTrim(next.profilePhotoResourceId ?? current?.profilePhotoResourceId),
    primaryDivisionId: divisions[0] ? divisionToDomainId(divisions[0]) : null,
  };
};

const openDownloadUrl = (url: string, fileName?: string) => {
  if (typeof window === 'undefined' || !url) return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  if (fileName) {
    anchor.download = fileName;
  }
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export function FacultyLiveSurface({
  mode,
}: {
  mode: 'directory' | 'profile' | 'teaching' | 'notes' | 'communications';
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ facultyId?: string }>();

  const schoolContext = useSchoolContext();
  const tenantSchools = schoolContext.schools ?? [];
  const activeSchoolId = schoolContext.activeSchoolId ?? '';
  const hasSchoolContext = Boolean(activeSchoolId);
  const facultyInviteLink = useMemo(() => {
    if (!activeSchoolId || typeof window === 'undefined') {
      return '';
    }
    const params = new URLSearchParams({
      mode: 'signup',
      accountType: 'staff',
      schoolId: activeSchoolId,
    });
    return `${window.location.origin}/auth?${params.toString()}`;
  }, [activeSchoolId]);

  const listQuery = useDomainRouterQuery(
    'listFaculty',
    { filter: { orgId: activeSchoolId, limit: 200 } },
    { skip: !hasSchoolContext },
  );
  const listItemsFromQuery = useMemo(
    () => asArray(asRecord(listQuery.data).items).map((item, index) => mapFacultyRecord(item, index, activeSchoolId)),
    [activeSchoolId, listQuery.data],
  );

  const [localFaculty, setLocalFaculty] = useState<FacultyRecord[]>([]);
  const [notesOverridesByFacultyId, setNotesOverridesByFacultyId] = useState<Record<string, NoteRecord[]>>({});
  const [communicationsOverridesByFacultyId, setCommunicationsOverridesByFacultyId] = useState<
    Record<string, CommunicationRecord[]>
  >({});
  const [selectedFacultyId, setSelectedFacultyId] = useState('');
  const [successToast, setSuccessToast] = useState('');

  const [createFacultyMutation] = useDomainRouterMutation('createFaculty');
  const [updateFacultyMutation] = useDomainRouterMutation('updateFaculty');
  const [addFacultyAssignmentMutation] = useDomainRouterMutation('addFacultyAssignment');
  const [addFacultyNoteMutation] = useDomainRouterMutation('addFacultyNote');
  const [sendFacultyCommunicationMutation] = useDomainRouterMutation('sendFacultyCommunication');
  const [requestFacultyAssetUploadMutation] = useDomainRouterMutation('requestFacultyAssetUpload');
  const [exportFacultyDirectoryMutation] = useDomainRouterMutation('exportFacultyDirectory');

  const showToast = (message: string, duration = 2500) => {
    setSuccessToast(message);
    window.setTimeout(() => setSuccessToast(''), duration);
  };

  const facultyRecords = useMemo(() => {
    const byId = new Map<string, FacultyRecord>();
    [...listItemsFromQuery, ...localFaculty].forEach((record) => {
      const existing = byId.get(facultyIdentityKey(record));
      byId.set(
        facultyIdentityKey(record),
        existing ? { ...existing, ...record } : record,
      );
    });
    return Array.from(byId.values()).sort((left, right) =>
      `${left.lastName} ${left.firstName}`.localeCompare(`${right.lastName} ${right.firstName}`),
    );
  }, [listItemsFromQuery, localFaculty]);

  const pathFacultyId = useMemo(() => {
    const explicitParam = readString(params.facultyId);
    if (explicitParam) return explicitParam;
    return inferFacultyIdFromPath(location.pathname);
  }, [location.pathname, params.facultyId]);

  useEffect(() => {
    if (pathFacultyId) {
      setSelectedFacultyId(pathFacultyId);
      return;
    }
    if (!selectedFacultyId && facultyRecords.length > 0) {
      setSelectedFacultyId(facultyRecords[0].id);
    }
  }, [facultyRecords, pathFacultyId, selectedFacultyId]);

  const activeFacultyId = pathFacultyId || selectedFacultyId || facultyRecords[0]?.id || '';

  const detailQuery = useDomainRouterQuery('getFaculty', { facultyId: activeFacultyId }, { skip: !activeFacultyId });
  const assignmentsQuery = useDomainRouterQuery(
    'listFacultyAssignments',
    { facultyId: activeFacultyId },
    { skip: !activeFacultyId },
  );
  const notesQuery = useDomainRouterQuery('listFacultyNotes', { facultyId: activeFacultyId }, { skip: !activeFacultyId });
  const communicationsQuery = useDomainRouterQuery(
    'listFacultyCommunications',
    { facultyId: activeFacultyId },
    { skip: !activeFacultyId },
  );

  const selectedFacultyBase = useMemo(
    () => facultyRecords.find((record) => record.id === activeFacultyId) ?? null,
    [activeFacultyId, facultyRecords],
  );

  const selectedDetailRecord = useMemo(() => {
    const detailRecord = asRecord(detailQuery.data);
    if (!Object.keys(detailRecord).length) return selectedFacultyBase;
    const mapped = mapFacultyRecord(detailRecord, 0, activeSchoolId);
    if (!selectedFacultyBase) return mapped;
    return {
      ...selectedFacultyBase,
      ...mapped,
      id: selectedFacultyBase.id,
      profilePhoto: mapped.profilePhoto || selectedFacultyBase.profilePhoto,
      profilePhotoResourceId: mapped.profilePhotoResourceId ?? selectedFacultyBase.profilePhotoResourceId,
    };
  }, [activeSchoolId, detailQuery.data, selectedFacultyBase]);

  const assignmentRecords = useMemo(
    () => mapAssignmentRecords(asArray(asRecord(assignmentsQuery.data).items)),
    [assignmentsQuery.data],
  );

  const noteRecords = useMemo(() => {
    const override = notesOverridesByFacultyId[activeFacultyId];
    if (override) return override;
    return mapNotes(asArray(asRecord(notesQuery.data).items));
  }, [activeFacultyId, notesOverridesByFacultyId, notesQuery.data]);

  const communicationRecords = useMemo(() => {
    const override = communicationsOverridesByFacultyId[activeFacultyId];
    if (override) return override;
    return mapCommunications(asArray(asRecord(communicationsQuery.data).items));
  }, [activeFacultyId, communicationsOverridesByFacultyId, communicationsQuery.data]);

  const directoryFaculty = useMemo(
    () => facultyRecords.map((record) => facultyRecordToFacultyMember(record)),
    [facultyRecords],
  );

  const selectedFaculty = useMemo(
    () =>
      selectedDetailRecord
        ? facultyRecordToFacultyMember(selectedDetailRecord, {
            assignments: assignmentRecords,
            notes: noteRecords,
            communications: communicationRecords,
          })
        : null,
    [assignmentRecords, communicationRecords, noteRecords, selectedDetailRecord],
  );

  const requireSchoolContext = () => {
    if (activeSchoolId) return activeSchoolId;
    throw new Error('Faculty requires an active school context before real data actions can run.');
  };

  const uploadFacultyAsset = async (
    facultyId: string,
    kind: UploadKind,
    file: File,
  ): Promise<FacultyAssetDescriptor> => {
    const upload = await requestFacultyAssetUploadMutation({
      input: {
        facultyId,
        kind,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      },
    });

    const uploadRecord = asRecord(upload);
    const uploadUrl = readString(uploadRecord.uploadUrl);
    if (!uploadUrl) {
      throw new Error('Faculty asset upload did not return an upload URL.');
    }

    const headers: Record<string, string> = {};
    if (file.type) {
      headers['Content-Type'] = file.type;
    }

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Faculty asset upload failed with HTTP ${response.status}.`);
    }

    return {
      resourceId: readString(uploadRecord.resourceId, `faculty-asset:${file.name}`),
      fileName: file.name,
      contentType: file.type || undefined,
      sizeBytes: file.size,
      storageKey: readString(uploadRecord.storageKey) || undefined,
      uploadedAt: new Date().toISOString(),
    };
  };

  const persistSupplementalAssignments = async (
    facultyId: string,
    updates: Partial<FacultyMember>,
  ) => {
    const assignments: Array<Promise<unknown>> = [];
    const now = Math.floor(Date.now() / 1000);
    const nextTeachingAssignment = updates.teachingAssignment;
    const divisions = uniqueStrings((updates.divisions ?? []).map((division) => divisionToDomainId(division)));

    divisions.forEach((divisionId, index) => {
      assignments.push(
        addFacultyAssignmentMutation({
          input: {
            facultyId,
            type: 'division',
            assignment: {
              divisionId,
              assignmentType: index === 0 ? 'primary' : 'secondary',
              isPrimary: index === 0,
              startAt: now,
              allowOverlap: true,
            },
          },
        }),
      );
    });

    nextTeachingAssignment?.classes.forEach((classroom) => {
      if (!classroom.sectionId && !classroom.courseId) return;
      assignments.push(
        addFacultyAssignmentMutation({
          input: {
            facultyId,
            type: 'teaching',
            assignment: {
              academicYear: '2025-2026',
              courseId: classroom.courseId ?? undefined,
              sectionId: classroom.sectionId ?? undefined,
              divisionLabel: nextTeachingAssignment.divisions[0] ?? undefined,
              gradeLabel: classroom.grade || undefined,
              subjectLabel: classroom.subject || undefined,
              homeroom: nextTeachingAssignment.homeroom ?? undefined,
              studentCount: classroom.studentCount,
              startAt: now,
            },
          },
        }),
      );
    });

    const persistSchedule = (
      schedule: TeachingAssignment['schedule'] | undefined,
      weekOffset: number,
    ) => {
      if (!schedule) return;
      const template = Object.entries(schedule).flatMap(([day, blocks]) =>
        blocks.map((block) => ({
          day: day.toLowerCase().slice(0, 3),
          period: block.period,
          startMinute: Math.max(0, (block.period - 1) * 60 + 480),
          endMinute: Math.max(1, block.period * 60 + 480),
          label: block.subject,
          location: block.room,
          blockType: block.type,
          gradeLabel: block.grade,
          subjectLabel: block.subject,
          room: block.room,
        })),
      );
      if (template.length === 0) return;

      assignments.push(
        addFacultyAssignmentMutation({
          input: {
            facultyId,
            type: 'schedule',
            assignment: {
              academicYear: '2025-2026',
              template: weekOffset === 0 ? template : [],
              exceptions: weekOffset === 0
                ? []
                : [{
                    date: Math.floor((startOfWeek(new Date()).getTime() + weekOffset * 7 * 24 * 60 * 60 * 1000) / 1000),
                    overrideBlocks: template,
                  }],
            },
          },
        }),
      );
    };

    persistSchedule(nextTeachingAssignment?.schedule, 0);
    persistSchedule(nextTeachingAssignment?.nextWeekSchedule, 1);

    const emergencyContact = updates.emergencyContact;
    if (emergencyContact?.name && emergencyContact.relationship && emergencyContact.phone) {
      assignments.push(
        addFacultyAssignmentMutation({
          input: {
            facultyId,
            type: 'emergency',
            assignment: {
              name: emergencyContact.name,
              relationship: emergencyContact.relationship,
              phone: emergencyContact.phone,
              alternatePhone: emergencyContact.alternatePhone || null,
              priority: 1,
              consentProvided: true,
            },
          },
        }),
      );
    }

    await Promise.all(assignments);
  };

  const handleNavigateToProfile = (facultyId: string) => {
    setSelectedFacultyId(facultyId);
    navigate(`/school/faculty/${encodeURIComponent(facultyId)}`);
  };

  const handleAddFaculty = async (newFaculty: FacultyProfileDraft) => {
    try {
      const schoolId = requireSchoolContext();
      const payload = buildProfileInput(newFaculty);
      const created = await createFacultyMutation({
        input: {
          orgId: schoolId,
          ...payload,
        },
      });

      const createdRecord = mapFacultyRecord(asRecord(created), facultyRecords.length, schoolId);

      let nextRecord = createdRecord;
      if (newFaculty.profilePhotoFile) {
        const descriptor = await uploadFacultyAsset(createdRecord.id, 'profile-photo', newFaculty.profilePhotoFile);
        const previewUrl = URL.createObjectURL(newFaculty.profilePhotoFile);
        const updated = await updateFacultyMutation({
          input: {
            facultyId: createdRecord.id,
            profilePhotoUrl: descriptor.storageKey ?? descriptor.resourceId,
            resourceId: descriptor.resourceId,
          },
        });
        nextRecord = {
          ...mapFacultyRecord(asRecord(updated), facultyRecords.length, schoolId),
          profilePhoto: previewUrl,
          profilePhotoResourceId: descriptor.resourceId,
        };
      }

      await persistSupplementalAssignments(createdRecord.id, newFaculty);
      setLocalFaculty((current) => upsertFacultyRecord(current, nextRecord));
      setSelectedFacultyId(createdRecord.id);
      showToast('Faculty member created successfully.');
      navigate(`/school/faculty/${encodeURIComponent(createdRecord.id)}`);
    } catch (error) {
      showToast(
        `Unable to create faculty member: ${String((error as Error | undefined)?.message ?? error ?? 'Unknown error')}`,
        3500,
      );
      throw error;
    }
  };

  const handleUpdateFaculty = async (facultyId: string, updates: FacultyProfileDraft) => {
    if (!selectedFaculty) return;

    try {
      let nextUpdates: Partial<FacultyMember> = { ...updates };
      if (updates.profilePhotoRemoved) {
        nextUpdates = {
          ...nextUpdates,
          profilePhoto: '',
          profilePhotoResourceId: '',
        };
      }

      if (updates.profilePhotoFile) {
        const descriptor = await uploadFacultyAsset(facultyId, 'profile-photo', updates.profilePhotoFile);
        nextUpdates = {
          ...nextUpdates,
          profilePhoto: URL.createObjectURL(updates.profilePhotoFile),
          profilePhotoResourceId: descriptor.resourceId,
        };
      }

      const payload = buildProfileInput(nextUpdates, selectedFaculty);
      const updated = await updateFacultyMutation({
        input: {
          facultyId,
          ...payload,
        },
      });

      await persistSupplementalAssignments(facultyId, nextUpdates);

      const updatedRecord = mergeFacultyRecord(
        {
          ...mapFacultyRecord(asRecord(updated), 0, activeSchoolId),
          profilePhoto: nextUpdates.profilePhoto ?? mapFacultyRecord(asRecord(updated), 0, activeSchoolId).profilePhoto,
          profilePhotoResourceId:
            nextUpdates.profilePhotoResourceId
            ?? mapFacultyRecord(asRecord(updated), 0, activeSchoolId).profilePhotoResourceId,
        },
        nextUpdates,
      );
      setLocalFaculty((current) => upsertFacultyRecord(current, updatedRecord));
      showToast('Faculty profile updated successfully.');
    } catch (error) {
      showToast(
        `Unable to update faculty profile: ${String((error as Error | undefined)?.message ?? error ?? 'Unknown error')}`,
        3500,
      );
      throw error;
    }
  };

  const handleBulkStatusUpdate = async (ids: string[], status: FacultyMember['status']) => {
    await Promise.all(ids.map((facultyId) =>
      updateFacultyMutation({
        input: {
          facultyId,
          status,
        },
      }),
    ));

    setLocalFaculty((current) => {
      const currentById = new Map(current.map((record) => [record.id, record]));
      const nextRecords = ids
        .map((facultyId) => {
          const baseRecord = currentById.get(facultyId) ?? facultyRecords.find((record) => record.id === facultyId);
          if (!baseRecord) return null;
          return { ...baseRecord, status };
        })
        .filter((entry): entry is FacultyRecord => Boolean(entry));

      const remaining = current.filter((record) => !ids.includes(record.id));
      return [...nextRecords, ...remaining];
    });
  };

  const handleExportFaculty = async (ids?: string[]) => {
    try {
      const schoolId = requireSchoolContext();
      const result = await exportFacultyDirectoryMutation({
        input: {
          orgId: schoolId,
          facultyIds: ids?.length ? ids : undefined,
        },
      });

      const resultRecord = asRecord(result);
      const downloadUrl = readString(resultRecord.downloadUrl);
      if (!downloadUrl) {
        throw new Error('Faculty export did not return a download URL.');
      }

      openDownloadUrl(downloadUrl, readString(resultRecord.fileName) || undefined);
    } catch (error) {
      showToast(
        `Unable to export faculty directory: ${String((error as Error | undefined)?.message ?? error ?? 'Unknown error')}`,
        3500,
      );
    }
  };

  const handleSaveNote = async (draft: FacultyNoteDraft) => {
    if (!activeFacultyId) return;

    try {
      const attachments = draft.attachmentFile
        ? [await uploadFacultyAsset(activeFacultyId, 'note-attachment', draft.attachmentFile)]
        : [];
      const saved = await addFacultyNoteMutation({
        input: {
          facultyId: activeFacultyId,
          body: draft.content,
          visibility: draft.isImportant ? 'shared' : 'internal',
          category: draft.category,
          isImportant: draft.isImportant,
          attachmentName: attachments[0]?.fileName,
          attachments,
        },
      });

      const nextNote = mapNotes([asRecord(saved)])[0];
      if (nextNote) {
        setNotesOverridesByFacultyId((current) => ({
          ...current,
          [activeFacultyId]: [...(current[activeFacultyId] ?? []), nextNote].sort(
            (left, right) => toDateMs(right.timestamp) - toDateMs(left.timestamp),
          ),
        }));
      }
      showToast('Note saved successfully.');
    } catch (error) {
      showToast(
        `Unable to save note: ${String((error as Error | undefined)?.message ?? error ?? 'Unknown error')}`,
        3500,
      );
      throw error;
    }
  };

  const handleComposeCommunication = async (draft: FacultyCommunicationDraft) => {
    if (!activeFacultyId) return;

    try {
      const attachments = draft.attachmentFile
        ? [await uploadFacultyAsset(activeFacultyId, 'communication-attachment', draft.attachmentFile)]
        : [];
      const saved = await sendFacultyCommunicationMutation({
        input: {
          facultyId: activeFacultyId,
          channel: 'email',
          recipient: draft.to,
          subject: draft.subject || 'Faculty update',
          body: draft.body,
          attachments,
        },
      });

      const nextCommunication = mapCommunications([asRecord(saved)])[0];
      if (nextCommunication) {
        setCommunicationsOverridesByFacultyId((current) => ({
          ...current,
          [activeFacultyId]: [nextCommunication, ...(current[activeFacultyId] ?? [])].sort(
            (left, right) => toDateMs(right.timestamp) - toDateMs(left.timestamp),
          ),
        }));
      }
      showToast('Communication queued successfully.');
    } catch (error) {
      showToast(
        `Unable to queue communication: ${String((error as Error | undefined)?.message ?? error ?? 'Unknown error')}`,
        3500,
      );
      throw error;
    }
  };

  const initialTab =
    mode === 'teaching'
      ? 'teaching'
      : mode === 'notes'
        ? 'notes'
        : mode === 'communications'
          ? 'communications'
          : 'overview';

  const directoryInitialLoading =
    mode === 'directory'
    && hasSchoolContext
    && facultyRecords.length === 0
    && !listQuery.isError
    && !listQuery.isSuccess
    && (listQuery.isUninitialized || listQuery.isLoading || listQuery.isFetching);

  if (schoolContext.status === 'loading') {
    return <SchoolContextRecoveryCard subject="Faculty" mode="loading" />;
  }

  if (schoolContext.status === 'error') {
    return (
      <SchoolContextRecoveryCard
        subject="Faculty"
        mode="failure"
        errorMessage={schoolContext.error}
      />
    );
  }

  if (schoolContext.status === 'no-membership') {
    return (
      <SchoolContextRecoveryCard subject="Faculty" mode="no-membership" />
    );
  }

  if (listQuery.isError && facultyRecords.length === 0) {
    return (
      <section className="mx-auto max-w-[960px] p-6 md:p-8">
        <SupportErrorMessage className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-coral">Route Data Failure</p>
          <p className="mt-3 text-base text-irongrey">
            {readErrorMessage(listQuery.error) || 'Unable to load faculty directory data.'}
          </p>
        </SupportErrorMessage>
      </section>
    );
  }

  if (schoolContext.status === 'selection-required') {
    return (
      <SchoolContextRecoveryCard
        subject="Faculty"
        mode="selection-required"
        schools={tenantSchools}
        isSwitching={schoolContext.isResolving}
        onSelectSchool={(schoolId) => {
          void schoolContext.selectSchool(schoolId);
        }}
      />
    );
  }

  if (directoryInitialLoading) {
    return (
      <FacultyDirectoryPage
        faculty={[]}
        isInitialLoading
        facultyInviteLink={facultyInviteLink}
        onNavigateToProfile={handleNavigateToProfile}
        onAddFaculty={handleAddFaculty}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onExportFaculty={(ids) => {
          void handleExportFaculty(ids);
        }}
        successToast={successToast}
      />
    );
  }

  if (mode === 'directory') {
    return (
      <FacultyDirectoryPage
        faculty={directoryFaculty}
        isInitialLoading={false}
        facultyInviteLink={facultyInviteLink}
        onNavigateToProfile={handleNavigateToProfile}
        onAddFaculty={handleAddFaculty}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onExportFaculty={(ids) => {
          void handleExportFaculty(ids);
        }}
        successToast={successToast}
      />
    );
  }

  if (!selectedFaculty) {
    return (
      <section className="mx-auto max-w-[960px] p-6 md:p-8">
        <div className="rounded-[1.5rem] border border-dashed border-gray-200 bg-white px-6 py-16 text-center text-text-secondary shadow-sm">
          No faculty profile is currently selected.
        </div>
      </section>
    );
  }

  return (
    <FacultyProfilePage
      faculty={selectedFaculty}
      onBack={() => navigate(DEFAULT_ROUTER_MODE_PATH)}
      onUpdateFaculty={handleUpdateFaculty}
      onSaveNote={handleSaveNote}
      onComposeCommunication={handleComposeCommunication}
      initialTab={initialTab}
      successToast={successToast}
    />
  );
}
